import React, { useState } from 'react';
import Modal from './Modal';
import { AVATARS } from '../constants';

const DURATION_OPTIONS = [
  { label: '1 minute',   value: 1  },
  { label: '2 minutes',  value: 2  },
  { label: '5 minutes',  value: 5  },
  { label: '10 minutes', value: 10 },
  { label: '15 minutes', value: 15 },
  { label: '20 minutes', value: 20 },
  { label: '30 minutes', value: 30 },
];

// ── LOGIN FORM ────────────────────────────────────────────────────────────────
function LoginForm({ onLogin, loginError, onSwitch }) {
  const [username, setUsername] = useState('');
  const [passkey,  setPasskey]  = useState('');
  const [duration, setDuration] = useState(5);

  return (
    <>
      <p style={{ color: '#888', fontSize: '0.85rem', textAlign: 'center', marginBottom: '20px' }}>
        Welcome back! Enter your credentials to continue.
      </p>

      <div className="form-group">
        <label className="form-label">Username</label>
        <input
          className="form-input"
          placeholder="e.g. optimus_prime"
          value={username}
          onChange={e => setUsername(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Password</label>
        <input
          className="form-input"
          type="password"
          placeholder="Enter your password"
          value={passkey}
          onChange={e => setPasskey(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onLogin(username, passkey, duration)}
        />
      </div>

      {/* Duration Dropdown */}
      <div className="form-group">
        <label className="form-label">⏱️ How long do you want to play?</label>
        <div style={{ position: 'relative' }}>
          <select
            value={duration}
            onChange={e => setDuration(Number(e.target.value))}
            style={{
              width: '100%',
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              padding: '12px 40px 12px 16px',
              borderRadius: '12px',
              fontFamily: 'Nunito, sans-serif',
              fontSize: '1rem',
              fontWeight: 700,
              outline: 'none',
              cursor: 'pointer',
              appearance: 'none',
              WebkitAppearance: 'none',
            }}
          >
            {DURATION_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}
                style={{ background: '#1A1A35', color: '#F0F0FF' }}>
                {opt.label}
              </option>
            ))}
          </select>
          {/* Custom dropdown arrow */}
          <div style={{
            position: 'absolute', right: '14px', top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--yellow)', fontSize: '1rem', pointerEvents: 'none',
          }}>▾</div>
        </div>
        <div style={{
          marginTop: '6px', fontSize: '0.78rem', color: 'var(--muted)',
          display: 'flex', alignItems: 'center', gap: '4px',
        }}>
          ⚡ Game ends when time runs out or energy hits 0 / 350
        </div>
      </div>

      {loginError && (
        <div style={{ color: '#FF4060', fontSize: '0.85rem', marginBottom: '12px', fontWeight: 700 }}>
          ⚠️ {loginError}
        </div>
      )}

      <button
        className="btn-primary"
        style={{ width: '100%', fontSize: '1rem', padding: '12px', marginBottom: '12px' }}
        onClick={() => onLogin(username, passkey, duration)}
      >
        🎮 Login & Play
      </button>

      <p style={{ textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>
        New here?{' '}
        <span onClick={onSwitch} style={{ color: '#FFD93D', cursor: 'pointer', fontWeight: 700 }}>
          Create an account
        </span>
      </p>
    </>
  );
}

// ── REGISTER FORM ─────────────────────────────────────────────────────────────
function RegisterForm({ onRegister, loginError, onSwitch }) {
  const [username, setUsername] = useState('');
  const [passkey,  setPasskey]  = useState('');
  const [avatarId, setAvatarId] = useState(1);
  const [duration, setDuration] = useState(5);
  const [error,    setError]    = useState('');

  const handleSubmit = () => {
    if (!username.trim())    return setError('Username is required!');
    if (username.length < 3) return setError('Username must be at least 3 characters!');
    if (!passkey.trim())     return setError('Password is required!');
    if (passkey.length < 4)  return setError('Password must be at least 4 characters!');
    setError('');
    onRegister({ username: username.trim().replace(/\s+/g, '_'), passkey, avatarId, duration });
  };

  const displayError = loginError || error;

  return (
    <>
      <p style={{ color: '#888', fontSize: '0.85rem', textAlign: 'center', marginBottom: '20px' }}>
        Welcome to Resource Rush! 🎮<br />Create your account to start playing.
      </p>

      <div className="form-group">
        <label className="form-label">Username</label>
        <input
          className="form-input"
          placeholder="e.g. optimus_prime"
          value={username}
          onChange={e => setUsername(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Password</label>
        <input
          className="form-input"
          type="password"
          placeholder="Min 4 characters"
          value={passkey}
          onChange={e => setPasskey(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Choose Your Avatar</label>
        <div className="avatar-grid">
          {AVATARS.map(av => (
            <div
              key={av.id}
              className={`avatar-option ${avatarId === av.id ? 'selected' : ''}`}
              onClick={() => setAvatarId(av.id)}
              style={{ background: av.bg }}
              title={av.label}
            >
              {av.emoji}
            </div>
          ))}
        </div>
      </div>

      {/* Duration Dropdown */}
      <div className="form-group">
        <label className="form-label">⏱️ How long do you want to play?</label>
        <div style={{ position: 'relative' }}>
          <select
            value={duration}
            onChange={e => setDuration(Number(e.target.value))}
            style={{
              width: '100%',
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              padding: '12px 40px 12px 16px',
              borderRadius: '12px',
              fontFamily: 'Nunito, sans-serif',
              fontSize: '1rem',
              fontWeight: 700,
              outline: 'none',
              cursor: 'pointer',
              appearance: 'none',
              WebkitAppearance: 'none',
            }}
          >
            {DURATION_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}
                style={{ background: '#1A1A35', color: '#F0F0FF' }}>
                {opt.label}
              </option>
            ))}
          </select>
          <div style={{
            position: 'absolute', right: '14px', top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--yellow)', fontSize: '1rem', pointerEvents: 'none',
          }}>▾</div>
        </div>
        <div style={{
          marginTop: '6px', fontSize: '0.78rem', color: 'var(--muted)',
          display: 'flex', alignItems: 'center', gap: '4px',
        }}>
          ⚡ Game ends when time runs out or energy hits 0 / 350
        </div>
      </div>

      {displayError && (
        <div style={{ color: '#FF4060', fontSize: '0.85rem', marginBottom: '12px', fontWeight: 700 }}>
          ⚠️ {displayError}
        </div>
      )}

      <button
        className="btn-primary"
        style={{ width: '100%', fontSize: '1rem', padding: '12px', marginBottom: '12px' }}
        onClick={handleSubmit}
      >
        🚀 Create Account & Play
      </button>

      <p style={{ textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>
        Already have an account?{' '}
        <span onClick={onSwitch} style={{ color: '#FFD93D', cursor: 'pointer', fontWeight: 700 }}>
          Login
        </span>
      </p>
    </>
  );
}

// ── MAIN MODAL ────────────────────────────────────────────────────────────────
export default function RegisterModal({ onClose, onLogin, onRegister, loginError, startTab = 'login' }) {
  const [tab, setTab] = useState(startTab);
  return (
    <Modal title={tab === 'login' ? '🔑 Login' : '🆕 Register'} onClose={onClose}>
      {/* Tab switcher */}
      <div style={{
        display: 'flex', gap: '8px', marginBottom: '20px',
        background: 'var(--bg3)', borderRadius: '12px', padding: '4px',
      }}>
        {['login', 'register'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            flex: 1, padding: '8px', borderRadius: '10px', border: 'none',
            cursor: 'pointer', fontFamily: 'Nunito', fontWeight: 800, fontSize: '0.9rem',
            background: tab === t ? 'var(--card)' : 'transparent',
            color: tab === t ? 'var(--yellow)' : 'var(--muted)',
            transition: 'all 0.2s',
          }}>
            {t === 'login' ? '🔑 Login' : '🆕 Register'}
          </button>
        ))}
      </div>

      {tab === 'login'
        ? <LoginForm    onLogin={onLogin}       loginError={loginError} onSwitch={() => setTab('register')} />
        : <RegisterForm onRegister={onRegister} loginError={loginError} onSwitch={() => setTab('login')} />
      }
    </Modal>
  );
}